import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { EViewSolarEstimationSolarProductCategory } from "../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import { formatWithCommas } from "../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import styles from "./recommended-solar-component-option.module.scss";

interface IProps {
  id: string;
  name: string;
  maxOperatingVoltage: number;
  price: number;
  currency: string;
  quantity: number;
  category: EViewSolarEstimationSolarProductCategory;
  isRecommended: boolean;
  onCountChange: (
    id: string,
    count: number,
    category: EViewSolarEstimationSolarProductCategory
  ) => void;
  validationMessage?: string;
  liters: number;
  ratedPower: number;
  amperage: number;
  batteryVoltage: number;
}

const RecommendedSolarComponentOption = (props: IProps) => {
  const {
    id,
    name,
    maxOperatingVoltage,
    price,
    currency,
    quantity,
    category,
    liters,
    ratedPower,
    amperage,
    batteryVoltage,
    isRecommended,
    validationMessage,
    onCountChange,
  } = props;

  const getDeviceContent = useMemo(() => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        return (
          <>
            <Row>
              <Col className="title-4 font-weight-500">{name}</Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        return (
          <>
            <Row>
              <Col className="title-4 font-weight-500">{name}</Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Tank Size : {liters}L
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.PANELS:
        return (
          <>
            <Row>
              <Col className="title-4 font-weight-500">{name}</Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Rated Power : {ratedPower}W
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        return (
          <>
            <Row>
              <Col className="title-4 font-weight-500">{name}</Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Max Operating Voltage : {maxOperatingVoltage}V
              </Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Amperage : {amperage}A
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        return (
          <>
            <Row>
              <Col className="title-4 font-weight-500">{name}</Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Rated Power : {ratedPower}W
              </Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Operating Voltage : {batteryVoltage}V
              </Col>
            </Row>
          </>
        );
    }
  }, [
    category,
    name,
    liters,
    ratedPower,
    maxOperatingVoltage,
    amperage,
    batteryVoltage,
  ]);

  return (
    <div
      className={`rounded-3 bg-nav-bar px-4 py-3 ${styles.recommendedSolarComponentOption}`}
    >
      {isRecommended && (
        <Row>
          <Col className="text-color-3 font-weight-500 font-size-12">
            Recommended
          </Col>
        </Row>
      )}
      <Row className="align-items-center mt-2 justify-content-between">
        <Col>
          <Row className="align-items-center">
            <Col className="col-auto">
              <MaterialIcon
                icon={
                  category ===
                  EViewSolarEstimationSolarProductCategory.INVERTERS
                    ? "tablet_mac"
                    : category ===
                      EViewSolarEstimationSolarProductCategory.BATTERIES
                    ? "battery_horiz_000"
                    : category ===
                      EViewSolarEstimationSolarProductCategory.PANELS
                    ? "solar_power"
                    : category ===
                      EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS
                    ? "google_tv_remote"
                    : category ===
                      EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                    ? "propane"
                    : "tune"
                }
                color="var(--color-component-icon)"
              />
            </Col>
            <Col xl={7}>{getDeviceContent}</Col>
            <Col className="text-color-5 font-weight-500 font-size-12">
              {currency}&nbsp;{formatWithCommas(Number(Helper.roundTo2(price)))}
            </Col>
          </Row>
        </Col>
        <Col className="col-auto">
          <Row className="align-items-center gap-5">
            {category !==
              EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS && (
              <Col className="col-auto">
                <Row className="align-items-center">
                  <Col className="col-auto">
                    <MaterialIcon
                      icon="remove"
                      color="#fff"
                      className={`${
                        quantity > 1
                          ? "bg-primary cursor-pointer"
                          : "bg-background-4 cursor-disabled"
                      } d-flex justify-content-center align-items-center rounded-circle`}
                      size={16}
                      style={{ width: "30px", height: "30px" }}
                      onClick={() => {
                        if (quantity > 1) {
                          onCountChange(
                            id,
                            quantity - 1,

                            category
                          );
                        }
                      }}
                    />
                  </Col>
                  <Col className="col-auto font-weight-500 text-label">
                    {quantity}
                  </Col>
                  <Col className="col-auto">
                    <MaterialIcon
                      icon="add"
                      color="#fff"
                      className="bg-primary d-flex justify-content-center align-items-center rounded-circle cursor-pointer"
                      size={16}
                      style={{ width: "30px", height: "30px" }}
                      onClick={() => {
                        onCountChange(id, quantity + 1, category);
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            )}
            <Col className="text-label font-weight-600">
              {currency}&nbsp;
              {formatWithCommas(Number(Helper.roundTo2(price * quantity)))}
            </Col>
          </Row>
        </Col>
      </Row>
      {validationMessage && (
        <Row className="mt-2">
          <Col>
            <div className="bg-background-13 text-brown font-size-12 px-4 py-2 rounded-3">
              {validationMessage}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default RecommendedSolarComponentOption;
